import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-hot-toast";

const PlaylistSelector = ({
  label = "Select Playlist",
  onSelectPlaylist,
  isDisabled = false,
  className,
}) => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);

  const fetchPlaylists = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/my-playlists`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const newPlaylists = res.data.playlists || [];
      setPlaylists((prev) => [...prev, ...newPlaylists]);
    } catch (error) {
      toast.error("Failed to load playlists.");
    } finally {
      setLoading(false);
    }
  }, [ loading]);

  useEffect(() => {
    fetchPlaylists();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playlistOptions = playlists.map((playlist) => ({
    value: playlist.playlist_slug,
    label: playlist.name,
    playlist,
  }));

  const handlePlaylistSelect = (selectedOption) => {
    if (selectedOption) {
      setSelectedPlaylist(selectedOption.playlist);
      onSelectPlaylist(selectedOption.playlist);
    } else {
      setSelectedPlaylist(null);
      onSelectPlaylist(null);
    }
  };

  const isDarkMode = document.documentElement.classList.contains("dark");

  const customStyles = {
    control: (base) => ({
      ...base,
      background: isDarkMode ? "rgb(252 231 243)" : "rgb(30 41 59)", // pink-100 or slate-800
      borderColor: isDarkMode ? "rgb(244 114 182)" : "rgb(51 65 85)", // pink-400 or slate-700
      boxShadow: "none",
      "&:hover": {
        borderColor: isDarkMode ? "rgb(244 114 182)" : "rgb(51 65 85)",
      },
    }),
    menu: (base) => ({
      ...base,
      background: isDarkMode ? "rgb(252 231 243)" : "rgb(30 41 59)",
      border: "none",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "190px",
    }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected
        ? isDarkMode
          ? "rgb(244 114 182)"
          : "rgb(74 222 128)"
        : state.isFocused
        ? isDarkMode
          ? "rgb(251 207 232)"
          : "rgb(51 65 85)"
        : isDarkMode
        ? "rgb(252 231 243)"
        : "rgb(30 41 59)",
      color: state.isSelected
        ? isDarkMode
          ? "rgb(255 255 255)"
          : "rgb(0 0 0)"
        : isDarkMode
        ? "rgb(157 23 77)"
        : "rgb(226 232 240)",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDarkMode ? "rgb(157 23 77)" : "rgb(226 232 240)",
    }),
    input: (base) => ({
      ...base,
      color: isDarkMode ? "rgb(157 23 77)" : "rgb(226 232 240)",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDarkMode ? "rgb(190 24 93)" : "rgb(148 163 184)",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: isDarkMode ? "rgb(190 24 93)" : "rgb(148 163 184)",
      "&:hover": {
        color: isDarkMode ? "rgb(157 23 77)" : "rgb(226 232 240)",
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: isDarkMode ? "rgb(190 24 93)" : "rgb(148 163 184)",
      "&:hover": {
        color: isDarkMode ? "rgb(157 23 77)" : "rgb(226 232 240)",
      },
    }),
  };

  return (
    <div className={`w-full ${className}`}>
      <Select
        inputId="playlist-select-menu"
        aria-label={label}
        options={playlistOptions}
        styles={customStyles}
        value={
          selectedPlaylist
            ? {
                value: selectedPlaylist.playlist_slug,
                label: selectedPlaylist.name,
                playlist: selectedPlaylist,
              }
            : null
        }
        onChange={handlePlaylistSelect}
        menuPlacement="auto"
        placeholder={
          selectedPlaylist
            ? `Selected playlist: ${selectedPlaylist.name}`
            : "Select playlist"
        }
        isLoading={loading}
        isDisabled={isDisabled}
        isClearable
        isSearchable
        className="text-slate-950 dark:text-pink-900 font-potta text-sm"
        classNamePrefix="react-select"
      />
    </div>
  );
};

export default PlaylistSelector;
